import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import VersionDisplay from './VersionDisplay';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon
} from '@mui/icons-material';

interface LoginProps {
  onLogin: (email: string, password: string) => Promise<{ success: boolean; message?: string; requiresVerification?: boolean }>;
  onResendVerification: (email: string) => Promise<boolean>;
  onSwitchToRegister: () => void;
  onSwitchToForgotPassword: () => void;
}

type LocationState = { registered?: boolean; email?: string } | null;

export default function Login({ onLogin, onResendVerification, onSwitchToRegister, onSwitchToForgotPassword }: LoginProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [justRegistered, setJustRegistered] = useState(false);

  useEffect(() => {
    const state = location.state as LocationState;
    if (!state?.registered) return;
    setJustRegistered(true);
    if (state.email) {
      setEmail(state.email);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');
    setRequiresVerification(false);

    try {
      const result = await onLogin(email, password);
      if (!result.success) {
        if (result.requiresVerification) {
          setRequiresVerification(true);
          setError(result.message || 'Email verification required. Please check your email and click the verification link before logging in.');
        } else {
          setError(result.message || 'Invalid email or password');
        }
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              TimeSentinel Portal Login
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Sign in to access the administration portal
            </Typography>
          </Box>

          {justRegistered ? (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setJustRegistered(false)}>
              <Typography variant="body2">
                Your account was created. Sign in with your email and password. If your organization requires it, verify
                your email from the link we sent you before logging in.
              </Typography>
            </Alert>
          ) : requiresVerification ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Email Verification Required</strong><br />
                We've sent a verification link to your email address. Please check your inbox and click the verification link before attempting to log in again.
              </Typography>
            </Alert>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : null}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              autoComplete="email"
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {requiresVerification && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={async () => {
                  const success = await onResendVerification(email);
                  if (success) {
                    setError('Verification email sent! Please check your inbox.');
                  }
                }}
                sx={{ mb: 2 }}
              >
                Resend Verification Email
              </Button>
            </Box>
          )}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <Link
                component="button"
                variant="body2"
                onClick={onSwitchToForgotPassword}
                sx={{ cursor: 'pointer' }}
              >
                Forgot password?
              </Link>
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/new')}
                sx={{ cursor: 'pointer' }}
              >
                Create account
              </Link>
            </Typography>
          </Box>
          
          {/* Version display */}
          <Box sx={{ textAlign: 'center', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <VersionDisplay variant="compact" showLabel={false} />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
} 